import { useState } from "react";
import { ChevronDown, ChevronUp, BarChart3, TrendingUp, PieChart as PieChartIcon } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { COLORS, STATUS_COLORS } from "./utils";

const SECTOR_COLORS = [COLORS.primary, COLORS.secondary, COLORS.green, COLORS.amber];

interface ChartSectionProps {
  monthlyChartData: { month: string; total: number; draft: number; complete: number; finalized: number }[];
  signupChartData: { month: string; count: number }[];
  statusPieData: { name: string; value: number }[];
  sectorBarData: { name: string; value: number }[];
  avgCompleteness: { status: string; avgCompleteness: number; count: number }[];
  assessmentTrendNote: { count: number; direction: "up" | "down" } | null;
}

type ChartKey = "assessmentTrends" | "userSignups" | "byStatus" | "bySector" | "avgCompleteness";

function CardHeader({
  icon: Icon,
  title,
  subtitle,
  badge,
  chartKey,
  expanded,
  onToggle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  chartKey: ChartKey;
  expanded: boolean;
  onToggle: (key: ChartKey) => void;
}) {
  return (
    <div className="px-6 py-5 border-b border-border/30 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-foreground">{title}</h3>
            {badge}
          </div>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <button
        onClick={() => onToggle(chartKey)}
        className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
        aria-label={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
    </div>
  );
}

function SmallCardHeader({
  icon: Icon,
  title,
  chartKey,
  expanded,
  onToggle,
}: {
  icon: React.ElementType;
  title: string;
  chartKey: ChartKey;
  expanded: boolean;
  onToggle: (key: ChartKey) => void;
}) {
  return (
    <div className="px-6 py-5 border-b border-border/30 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-[35px] h-[35px] bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <h3 className="text-base font-bold text-foreground">{title}</h3>
      </div>
      <button
        onClick={() => onToggle(chartKey)}
        className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
        aria-label={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
    </div>
  );
}

const cardClass =
  "bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden";

export function ChartSection({
  monthlyChartData,
  signupChartData,
  statusPieData,
  sectorBarData,
  avgCompleteness,
  assessmentTrendNote,
}: ChartSectionProps) {
  const [expanded, setExpanded] = useState<Record<ChartKey, boolean>>({
    assessmentTrends: true,
    userSignups: true,
    byStatus: true,
    bySector: true,
    avgCompleteness: true,
  });

  const toggle = (key: ChartKey) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const trendBadge = assessmentTrendNote ? (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
        assessmentTrendNote.direction === "up"
          ? "text-green-600 bg-green-50"
          : "text-amber-600 bg-amber-50"
      }`}
    >
      {assessmentTrendNote.direction === "up"
        ? `↑ ${assessmentTrendNote.count} vs last month`
        : `↓ ${assessmentTrendNote.count} vs last month`}
    </span>
  ) : null;

  return (
    <div className="space-y-6">
      {/* Row 1: Assessment Trends + User Signups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={cardClass}>
          <CardHeader
            icon={BarChart3}
            title={"Assessment Trends"}
            subtitle={"Monthly assessments by status (last 12 months)"}
            badge={trendBadge}
            chartKey="assessmentTrends"
            expanded={expanded.assessmentTrends}
            onToggle={toggle}
          />
          {expanded.assessmentTrends && (
            <div className="p-6">
              {monthlyChartData.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{"No data yet"}</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="draft" name={"Draft"} fill={COLORS.amber} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="complete" name={"Complete"} fill={COLORS.blue} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="finalized" name={"Finalized"} fill={COLORS.green} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>

        <div className={cardClass}>
          <CardHeader
            icon={TrendingUp}
            title={"User Signups"}
            subtitle={"Monthly new user registrations (last 12 months)"}
            chartKey="userSignups"
            expanded={expanded.userSignups}
            onToggle={toggle}
          />
          {expanded.userSignups && (
            <div className="p-6">
              {signupChartData.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{"No data yet"}</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={signupChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name={"Signups"}
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      dot={{ fill: COLORS.primary, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Status Pie + Sector Bar + Avg Completeness */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={cardClass}>
          <SmallCardHeader
            icon={PieChartIcon}
            title={"Assessments by Status"}
            chartKey="byStatus"
            expanded={expanded.byStatus}
            onToggle={toggle}
          />
          {expanded.byStatus && (
            <div className="p-6">
              {statusPieData.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{"No data"}</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {statusPieData.map((entry) => (
                        <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || COLORS.muted} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>

        <div className={cardClass}>
          <SmallCardHeader
            icon={BarChart3}
            title={"Assessments by Sector"}
            chartKey="bySector"
            expanded={expanded.bySector}
            onToggle={toggle}
          />
          {expanded.bySector && (
            <div className="p-6">
              {sectorBarData.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{"No data"}</p>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(sectorBarData.length * 50, 150)}
                >
                  <BarChart data={sectorBarData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                      {sectorBarData.map((_, i) => (
                        <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>

        <div className={cardClass}>
          <SmallCardHeader
            icon={BarChart3}
            title={"Avg. Completeness by Status"}
            chartKey="avgCompleteness"
            expanded={expanded.avgCompleteness}
            onToggle={toggle}
          />
          {expanded.avgCompleteness && (
            <div className="p-6">
              {avgCompleteness.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{"No data"}</p>
              ) : (
                <div className="space-y-4 pt-2">
                  {avgCompleteness.map((item) => (
                    <div key={item.status} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize font-medium text-slate-700">{item.status}</span>
                        <span className="text-slate-500">{`${item.avgCompleteness}% avg (${item.count})`}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div
                          className="h-2.5 rounded-full transition-all"
                          style={{
                            width: `${item.avgCompleteness}%`,
                            backgroundColor: STATUS_COLORS[item.status] || COLORS.muted,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
